import { useEffect } from 'react';

import { getTaxableOrderItem, isStorageOrderItem } from '@/components/modals/SubmitOrder/constants';
import { useOrderContext, useOrderItemsTotalCost } from '@/components/modals/SubmitOrder/helpers';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { ContactVm, MediaType } from '@/types';
import { formatPriceWithCurrency } from '@/utils/format-price';

import { OrderDetails } from '../../../Common/OrderDetails';
import { ProposalBlockWrapper } from '../../../Common/ProposalBlockWrapper';
import { getNonEquipmentItems, separateNonEquipmentItems } from '../../helpers';
import { ConfirmationContentDetails } from '../ConfirmationContentDetails';

import { Props } from './types';

import styles from '../../ConfirmationStep.module.scss';

export const ConfirmationCostDetails = ({
  calculatedTax,
  proposal,
  orderTotal,
  calculateTax,
  isActive,
}: Props) => {
  const state = useOrderContext();
  const { isDistribution, isAccessProgram } = state;
  const mediaType = useDeviceDetection();

  const { items: storage, total: storageTotal } = useOrderItemsTotalCost(state, isStorageOrderItem);
  const nonEquipmentItems = getNonEquipmentItems(state);
  const proposalAdders = proposal?.adders.map(proposalAdder => proposalAdder.name) ?? [];
  const { services, adders, servicesTotal, addersTotal } = separateNonEquipmentItems(
    state,
    nonEquipmentItems,
    proposalAdders,
  );

  const finalTotal = (orderTotal ?? 0) + (calculatedTax ?? 0);
  const estimatedTaxesTitle = mediaType === MediaType.Mobile ? 'Est. Taxes' : 'Estimated Taxes';

  useEffect(() => {
    if (isDistribution && isActive && orderTotal && orderTotal > 0) {
      const orderItems = [...services, ...adders, ...storage];

      const taxableOrderItems = orderItems.map(taxableItem =>
        getTaxableOrderItem(state, taxableItem, proposal),
      );

      calculateTax(state.order.shipmentContact as ContactVm, taxableOrderItems, proposal?.id ?? '');
    }
  }, [isActive]);

  if (!isDistribution) {
    return null;
  }

  return (
    <ProposalBlockWrapper title="Cost detail">
      {isAccessProgram && (
        <ConfirmationContentDetails
          data-testid="gafe-cost-detail-storage"
          itemsToRender={storage}
          itemsTotal={storageTotal.value}
          proposal={proposal}
          title="Storage"
        />
      )}

      <ConfirmationContentDetails
        itemsToRender={services}
        itemsTotal={servicesTotal}
        proposal={proposal}
        title="Services"
      />

      <ConfirmationContentDetails
        itemsToRender={adders}
        itemsTotal={addersTotal}
        proposal={proposal}
        title="Adders"
      />

      {!state.isPriceHidden && (
        <>
          <OrderDetails title={estimatedTaxesTitle} value={calculatedTax ?? 0} />
          <div className={styles.order_total}>
            <span>Total Cost:</span>
            <span>{formatPriceWithCurrency(finalTotal)}</span>
          </div>
        </>
      )}
    </ProposalBlockWrapper>
  );
};
